import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, MenuItem, Select, styled } from '@mui/material';

import CommentUpdateModal from '../Modals/CommentUpdateModal';
import CommentDeleteModal from '../Modals/CommentDeleteModal';

import { useGetMCTColumns } from './useGetMCTColumns';
import FilterTrapHistoryDataTable from './HistoryTable/FilterTrapHistoryDataTable';

import useGetTrapActivationHistory from 'hooks/Traps/Trap/useGetTrapActivationHistory';
import useScreenSize from 'hooks/Screen/useScreenSize';
import { TrapActivationHistory } from 'api/traps/types';
import TrapHistoryExpandable from 'components/TrapsTables/MultiCatchMouseTraps/TrapHistoryExpandable';
import SetActivationSignalStatusModal from 'pages/DevicesDetail/MCT/Modal/SetActivationSignalStatusModal';

interface TrapHistoryCardProps {
  slug: string;
  type: string;
}

const CustomSelect = styled(Select)({
  '&.MuiInputBase-root': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  '& .MuiSelect-select': {
    padding: '10px 12px',
    paddingRight: '24px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #E0E0E0',
  },
});

const TrapHistoryCard: React.FC<TrapHistoryCardProps> = ({ slug, type }) => {
  const filterValues = [
    { value: 'ALL', label: 'All Events' },
    { value: 'APPROVED_MICE', label: 'Only captured events' },
    { value: 'APPROVED_NOT_MICE', label: `Only non-${type === 'srst' ? 'rat' : 'mice'} captured events` },
    { value: 'CAUGHT_EMPTY', label: 'Only empty capture events' },
  ];

  const today = new Date();
  const lastYear = new Date(today);
  lastYear.setFullYear(today.getFullYear() - 1);
  const [startDate, setStartDate] = useState<string>(lastYear.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(today.toISOString().split('T')[0]);
  const [page, setPage] = useState(1);
  const [ordering, setQueryParam] = useState('');
  const [search, setSearch] = useState('');
  const [filteringData, setFilteringData] = useState<TrapActivationHistory[]>([]);
  const [filterValue, setFilterValue] = useState<string>('ALL');
  const { data, refetch, isLoading } = useGetTrapActivationHistory(slug || '', type, startDate, endDate);
  const columns = useGetMCTColumns(type);
  const searchFields = ['msg_token', 'order_datetime'];
  const { isNotLargeDesktop } = useScreenSize();
  const { t } = useTranslation();
  useEffect(() => {
    refetch();
  }, [page, search, ordering, refetch]);

  useEffect(() => {
    if (filterValue === 'ALL') {
      setFilteringData(data!);
    } else {
      setFilteringData(data!.filter(item => item.approv_status === filterValue) || []);
    }
  }, [data, filterValue]);

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card shadow-lg">
          <div className="card-header">
            <div className="row align-items-center gy-3">
              <div className="col-lg-8 col-md-12 d-flex flex-wrap gap-2 justify-content-center justify-content-lg-start">
                <input
                  type="date"
                  className="form-control"
                  style={{ width: 'auto' }}
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
                <input
                  type="date"
                  className="form-control"
                  style={{ width: 'auto' }}
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
              </div>

              <div className="col-lg-4 col-md-12 d-flex justify-content-center justify-content-lg-end">
                <FormControl sx={{ minWidth: 250 }}>
                  <InputLabel shrink sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Activity Type
                  </InputLabel>
                  <CustomSelect
                    value={filterValue || ''}
                    onChange={(event: any) => handleFilterChange(event.target.value as string)}
                    label="Activity Type"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      '&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--vz-purple)',
                      },
                    }}
                  >
                    {filterValues?.map(key => (
                      <MenuItem key={key.value} value={key.value} sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {key.label}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </FormControl>
              </div>
            </div>
          </div>
          <div className="card-body" style={{ minHeight: '520px' }}>
            <FilterTrapHistoryDataTable<TrapActivationHistory>
              columns={columns}
              title={t('MCTDetail.TrapHistoryCard.title')}
              titlePerPage={t('MCTDetail.TrapHistoryCard.titlePerPage')}
              data={filteringData || []}
              onChangePage={setPage}
              onChangeFilterParams={setQueryParam}
              onChangeSearch={setSearch}
              searchFields={searchFields}
              isLoading={isLoading}
              expandableRows={isNotLargeDesktop}
              expandableRowsComponent={row => <TrapHistoryExpandable data={row.data} />}
            />
            <SetActivationSignalStatusModal type={type} refetchActivationSignals={refetch} />
            <CommentUpdateModal />
            <CommentDeleteModal />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrapHistoryCard;
